import { Filterable, isRangeOperator, TCondition, TGroup } from '../types';
import { Suggestion } from './context';
import { ApiCondition, ApiGroup } from './types';

export const toApiFilter = (
  filter?: TGroup,
  filterNonValid: boolean = true
): ApiGroup | undefined => {
  if (!filter) {
    return undefined;
  }

  const apiGroup: ApiGroup = {
    type: 'GROUP',
    operator: filter.operator,
    values: filter.values
      .map((v) => {
        if (v.type === 'CONDITION') {
          // the backend only evaluates IS and IS NOT; a range condition
          // would silently be treated as IS NOT, so refuse it outright
          if (isRangeOperator(v.operator)) {
            throw new Error(
              `Range operator ${v.operator} is not supported by API filters (condition on "${v.key}")`
            );
          }
          if (filterNonValid && !isCompleteCondition(v)) {
            return undefined;
          }
          return {
            type: 'CONDITION',
            key: v.key,
            values: v.values.map((it) => ({
              key: it.filterObject.key,
              value: it.filterObject.value,
            })),
            operator: v.operator,
          };
        } else {
          return toApiFilter(v, filterNonValid);
        }
      })
      // Filter out undefined or sanitised values
      .filter((x) => !filterNonValid || !!x) as (ApiCondition | ApiGroup)[],
  };

  if (filterNonValid && apiGroup.values.length === 0) {
    return undefined;
  }

  return apiGroup;
};

const isCompleteCondition = (condition: TCondition) => {
  return (
    !!condition?.values?.length &&
    condition.values.every((v) => v.filterObject.key && v.filterObject.value)
  );
};

// Strips the parts of an ApiGroup the backend cannot evaluate: conditions
// with no key or no values (what the editor emits for a freshly added, still
// blank row) and groups left empty once those are gone. Returns undefined
// when nothing remains, i.e. "no filter". Use this when a filter edited
// through one of the *ApiFilter components (which keep incomplete conditions
// so the editor can keep showing them) is about to be sent to the server.
export const sanitizeApiFilter = (
  filter: ApiGroup | undefined
): ApiGroup | undefined => {
  if (!filter) {
    return undefined;
  }
  const values = filter.values
    .map((v) =>
      v.type === 'CONDITION'
        ? isCompleteApiCondition(v)
          ? v
          : undefined
        : sanitizeApiFilter(v)
    )
    .filter((v): v is ApiCondition | ApiGroup => !!v);
  if (values.length === 0) {
    return undefined;
  }
  return { ...filter, values };
};

const isCompleteApiCondition = (condition: ApiCondition): boolean => {
  return (
    !!condition.key &&
    !!condition.values?.length &&
    condition.values.every((v) => v.key && v.value)
  );
};

export const fromApiFilter = (
  suggestions: Suggestion[],
  selectedSuggestionConverter: (s: Filterable) => Filterable,
  filter?: ApiGroup,
  depth?: number
): TGroup | undefined => {
  if (!filter) {
    return undefined;
  }
  const currentDepth = depth ?? 0;

  return {
    index: depth ?? 0,
    type: 'GROUP',
    operator: filter.operator,
    // @ts-expect-error
    values: filter.values.map((v, index) => {
      const idx = (currentDepth + 1) * 100 + index;
      if (v.type === 'CONDITION') {
        return {
          index: idx,
          type: 'CONDITION',
          key: v.key,
          values: v.values
            .map((it) => {
              const suggestion = suggestions.find(
                (s) =>
                  s.filterObject.key === it.key &&
                  s.filterObject.value === it.value
              );
              return suggestion
                ? selectedSuggestionConverter(suggestion)
                : undefined;
            })
            // Exclude stuff that doesn't exist anymore
            .filter((x) => !!x),
          operator: v.operator,
        };
      } else {
        return (
          fromApiFilter(suggestions, selectedSuggestionConverter, v, idx) ?? []
        );
      }
    }),
  };
};
